/**
 * Maps the Filters modal's filterDraft fields and values to the wire-format values the Milestone 3
 * Filter Modal tracking plan expects (Confluence 5839192072, Filter Modal footer comment
 * 6254657537). Shared by segmentEventBuilder.js and googleAnalytics4EventBuilder.js so the two
 * providers only differ where the plan says they do (housing type's name).
 *
 * @module nativeInteractiveSiteMap/services/filterTrackingValues
 */

/**
 * filterDraft key → `filter_type`/`filter_name` value for the filters both providers name the same.
 * @type {Record<string, string>}
 */
const SHARED_FILTER_TYPES = {
  price: 'price',
  bedroom: 'bedrooms',
  bathroom: 'bathrooms',
  squareFeet: 'sqft',
  moveInDateRange: 'move_in',
};

/**
 * Housing type is the one filter the plan names differently per provider: GA4 reuses its existing
 * `home_type` dimension, Segment calls it `housing_type`. The same name is the Filter Applied
 * property key for that field.
 */
export const GOOGLE_ANALYTICS_4_HOUSING_TYPE_NAME = 'home_type';
export const SEGMENT_HOUSING_TYPE_NAME = 'housing_type';

/** @type {Record<string, string>} */
const GOOGLE_ANALYTICS_4_FILTER_NAMES = { ...SHARED_FILTER_TYPES, housingType: GOOGLE_ANALYTICS_4_HOUSING_TYPE_NAME };

/** @type {Record<string, string>} */
const SEGMENT_FILTER_TYPES = { ...SHARED_FILTER_TYPES, housingType: SEGMENT_HOUSING_TYPE_NAME };

/**
 * Whether the tracking plan reports edits to this filterDraft field. Both providers track the same
 * set of fields, only their names differ.
 * @param {string} field - a filterDraft key
 * @returns {boolean}
 */
export function isTrackedFilterField(field) {
  return field in SEGMENT_FILTER_TYPES;
}

/**
 * GA4 `filter_name` value.
 * @param {string} field - a filterDraft key
 * @returns {string|null}
 */
export function getGoogleAnalytics4FilterName(field) {
  return GOOGLE_ANALYTICS_4_FILTER_NAMES[field] ?? null;
}

/**
 * Segment `filter_type` value.
 * @param {string} field - a filterDraft key
 * @returns {string|null}
 */
export function getSegmentFilterType(field) {
  return SEGMENT_FILTER_TYPES[field] ?? null;
}

/**
 * A `{ minimum, maximum }` range as one string: '3+' (open-ended), '3' (exact), '3-4', '0-500000'
 * (no minimum). null when no bound is set ("Any").
 *
 * @param {{ minimum: number|null, maximum: number|null }} range
 * @returns {string|null}
 */
export function formatRange({ minimum, maximum } = {}) {
  if (minimum == null && maximum == null) {
    return null;
  }

  if (maximum == null) {
    return `${minimum}+`;
  }

  if (minimum === maximum) {
    return `${minimum}`;
  }

  return `${minimum ?? 0}-${maximum}`;
}
